import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Avatar,
  Button,
  Tag,
  Alert,
  Descriptions,
  Spin,
  Input,
  Modal,
  App,
} from "antd";
import {
  ArrowLeftOutlined,
  VideoCameraOutlined,
  CloseOutlined,
  EditOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  CalendarOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import {
  apiGetAppointmentDetails,
  apiCancelAppointment,
  apiUpdateConsultationNote,
} from "../../api/appointments";

const { TextArea } = Input;

const AppointmentDetails = () => {
  const { message } = App.useApp();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [appointment, setAppointment] = useState(null);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [consultationNotes, setConsultationNotes] = useState("");
  const [cancelModalVisible, setCancelModalVisible] = useState(false);

  const fetchAppointmentDetails = async () => {
    try {
      setLoading(true);
      const response = await apiGetAppointmentDetails(id);
      const appointmentData = response;

      setAppointment(appointmentData);
      setConsultationNotes(appointmentData?.doctorNotes || "");
    } catch (error) {
      console.error("Error fetching appointment details:", error);
      message.error("Không thể tải thông tin chi tiết cuộc hẹn");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointmentDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Handle save consultation notes
  const handleSaveNotes = async () => {
    try {
      await apiUpdateConsultationNote({
        appointment_id: parseInt(appointment?.appointmentId),
        doctor_notes: consultationNotes,
      });
      message.success("Ghi chú tư vấn đã được lưu thành công");
      setIsEditingNotes(false);
      setAppointment((prev) => ({
        ...prev,
        consultationNotes,
      }));
    } catch (error) {
      console.error("Error saving notes:", error);
      message.error("Không thể lưu ghi chú tư vấn");
    }
  };

  // Handle cancel appointment
  const handleCancelAppointment = async () => {
    try {
      await apiCancelAppointment(id);
      message.success("Cuộc hẹn đã được hủy thành công");
      setCancelModalVisible(false);
      navigate("/appointments");
    } catch (error) {
      console.error("Error cancelling appointment:", error);
      message.error("Không thể hủy cuộc hẹn");
    }
  };

  const handleJoinVideoCall = async () => {
    try {
      // Navigate to video call page with appointment ID
      navigate(`/video-call/${id}`);
    } catch (error) {
      console.error("Error joining video call:", error);
      message.error("Không thể tham gia cuộc gọi video");
    }
  };

  // Get status text and color
  const getStatusInfo = (status) => {
    const statusMap = {
      1: { text: "Đã xác nhận", color: "green" },
      2: { text: "Tạm hoãn", color: "orange" },
      3: { text: "Đã hủy", color: "red" },
      4: { text: "Hoàn thành", color: "blue" },
    };
    return statusMap[status] || { text: "Unknown", color: "default" };
  };

  // Format timestamp to readable date/time
  const formatDateTime = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (startTimestamp, endTimestamp) => {
    if (!startTimestamp || !endTimestamp) return "N/A";
    const startDate = new Date(startTimestamp * 1000);
    const endDate = new Date(endTimestamp * 1000);

    const startTime = startDate.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    const endTime = endDate.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    return `${startTime} - ${endTime}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (!appointment) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <Card>
          <div className="text-center py-12">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              Không tìm thấy cuộc hẹn
            </h2>
            <p className="text-gray-500 mb-6">
              Không tồn tại cuộc hẹn có mã {id}.
            </p>
            <Button type="primary" onClick={() => navigate("/appointments")}>
              Trở lại trang Lịch hẹn
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const statusInfo = getStatusInfo(appointment.status);

  // Generate alerts based on appointment data
  const generateAlerts = () => {
    const alerts = [];

    // Weight Goal Progress Alert
    if (appointment.weightGoalProgress) {
      alerts.push({
        type: appointment.weightGoalProgress.isOnTrack ? "success" : "warning",
        icon: <CheckCircleOutlined />,
        title: "Tiến độ đạt mục tiêu cân nặng",
        description: appointment.weightGoalProgress.isOnTrack
          ? `Đúng hướng - ${appointment.weightGoalProgress.progressPercentage}% đến mục tiêu`
          : `Chậm tiến độ - ${appointment.weightGoalProgress.progressPercentage}% đến mục tiêu`,
        color: appointment.weightGoalProgress.isOnTrack ? "#d1fae5" : "#fef3c7",
        borderColor: appointment.weightGoalProgress.isOnTrack
          ? "#10b981"
          : "#f59e0b",
      });
    }

    // Next Appointment Alert
    if (
      appointment.nextAppointment &&
      appointment.nextAppointment.appointmentTime
    ) {
      const nextDate = new Date(
        appointment.nextAppointment.appointmentTime * 1000
      );
      alerts.push({
        type: "info",
        icon: <CalendarOutlined />,
        title: "Cuộc hẹn tiếp theo",
        description: nextDate.toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
        color: "#dbeafe",
        borderColor: "#3b82f6",
      });
    }

    return alerts;
  };

  const alerts = generateAlerts();

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Back Button */}
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate("/appointments")}
        className="mb-4"
        type="text"
      >
        Back
      </Button>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">
          Chi tiết cuộc hẹn
        </h1>
        <p className="text-gray-500 mt-1">
          Xem thông tin tóm tắt về các cuộc tư vấn đã lên lịch
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Patient Info Card */}
          <Card className="shadow-sm rounded-lg">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-4">
                <Avatar
                  size={64}
                  style={{ backgroundColor: "#8B5CF6" }}
                  className="text-xl"
                >
                  {appointment.fullName?.charAt(0)?.toUpperCase()}
                </Avatar>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {appointment.fullName}
                  </h2>
                  <p className="text-gray-500">{appointment.subject}</p>
                  <p className="text-sm text-gray-400 mt-1">
                    {appointment.patientDisplayId}
                  </p>
                </div>
              </div>
              <Tag
                color={statusInfo.color}
                className="text-sm px-3 py-1 rounded-full"
              >
                {statusInfo.text}
              </Tag>
            </div>

            {/* Date, Time, Duration */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div>
                <p className="text-xs text-gray-500 mb-1">Ngày</p>
                <p className="text-sm font-medium text-gray-900">
                  {formatDateTime(appointment.date)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Thời gian</p>
                <p className="text-sm font-medium text-gray-900">
                  {formatTime(appointment.startDt, appointment.endDt)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Khoảng thời gian</p>
                <p className="text-sm font-medium text-gray-900">
                  {appointment.duration} phút
                </p>
              </div>
            </div>

            {/* Ready to Start Call Alert */}
            {appointment.joinVideoCall && (
              <Alert
                message="Bạn đã sẵn sàng bắt đầu cuộc gọi chưa?"
                description="Bệnh nhân sẽ được thông báo khi bạn tham gia cuộc tư vấn qua video."
                type="info"
                icon={<InfoCircleOutlined />}
                showIcon
                className="mb-0"
              />
            )}
          </Card>

          {/* Actions Card */}
          <Card title="Actions" className="shadow-sm rounded-lg">
            <div className="flex gap-3">
              <Button
                type="primary"
                icon={<VideoCameraOutlined />}
                size="large"
                onClick={handleJoinVideoCall}
                disabled={!appointment.joinVideoCall}
                className="bg-purple-600 hover:bg-purple-700 border-purple-600"
              >
                Tham gia cuộc gọi video
              </Button>
              <Button
                danger
                icon={<CloseOutlined />}
                size="large"
                onClick={() => setCancelModalVisible(true)}
                disabled={!appointment.cancelAppointment}
              >
                Hủy bỏ
              </Button>
            </div>
          </Card>

          {/* Consultation Notes Card */}
          <Card
            title="Ghi chú tư vấn"
            extra={
              <Button
                variant="link"
                color="primary"
                icon={isEditingNotes ? <SaveOutlined /> : <EditOutlined />}
                className="text-purple-600"
                onClick={() => {
                  if (isEditingNotes) {
                    handleSaveNotes();
                  } else {
                    setIsEditingNotes(true);
                  }
                }}
              >
                {isEditingNotes ? "Lưu" : "Sửa"}
              </Button>
            }
            className="shadow-sm rounded-lg"
          >
            {isEditingNotes ? (
              <TextArea
                value={consultationNotes}
                onChange={(e) => setConsultationNotes(e.target.value)}
                placeholder="Nhập ghi chú tư vấn..."
                autoSize={{ minRows: 4, maxRows: 8 }}
                className="text-sm"
              />
            ) : (
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                {consultationNotes ||
                  "Hiện chưa có ghi chú tư vấn nào. Vui lòng nhấp vào Chỉnh sửa để thêm ghi chú."}
              </p>
            )}
          </Card>
        </div>

        {/* Right Column - Patient Record & Alerts */}
        <div className="space-y-6">
          {/* Patient Record Card */}
          <Card
            title={
              <div className="flex items-center justify-between">
                <span>Hồ sơ bệnh nhân</span>
                <Button
                  variant="link"
                  color="primary"
                  size="small"
                  className="text-purple-600"
                  onClick={() => navigate(`/patients/${appointment.patientId}`)}
                >
                  Xem toàn bộ hồ sơ
                </Button>
              </div>
            }
            className="shadow-sm rounded-lg"
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Tuổi">
                {appointment.age > 0 ? `${appointment.age} name` : "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Chiều cao">
                {appointment.height || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Trọng lượng hiện tại">
                {appointment.currentWeight
                  ? `${appointment.currentWeight} lbs`
                  : "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="BMI">
                {appointment.bmi || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Cân nặng mục tiêu">
                {appointment.goalWeight
                  ? `${appointment.goalWeight} lbs`
                  : "N/A"}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Current Medications Card */}
          <Card title="Current Medications" className="shadow-sm rounded-lg">
            {appointment.currentMedications &&
            appointment.currentMedications.length > 0 ? (
              <ul className="space-y-2">
                {appointment.currentMedications.map((med, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-purple-600 mt-1">•</span>
                    <span className="text-sm text-gray-700">{med}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">Hiện không sử dụng thuốc nào</p>
            )}
          </Card>

          {/* Alerts & Reminders Card */}
          {alerts.length > 0 && (
            <Card title="Alerts & Reminders" className="shadow-sm rounded-lg">
              <div className="space-y-3">
                {alerts.map((alert, index) => (
                  <div
                    key={index}
                    className="p-3 rounded-lg border-l-4"
                    style={{
                      backgroundColor: alert.color,
                      borderLeftColor: alert.borderColor,
                    }}
                  >
                    <div className="flex items-start gap-2">
                      <span
                        style={{ color: alert.borderColor }}
                        className="text-lg"
                      >
                        {alert.icon}
                      </span>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-900">
                          {alert.title}
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          {alert.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Cancel Appointment Modal */}
      <Modal
        open={cancelModalVisible}
        onCancel={() => setCancelModalVisible(false)}
        footer={null}
        centered
        width={500}
        closable={false}
        className="cancel-appointment-modal"
      >
        <div className="text-center py-4">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Hủy bỏ</h2>
          <p className="text-gray-600 mb-6">
            Bạn có chắc chắn muốn hủy cuộc hẹn không?
          </p>

          <div className="flex gap-3 justify-center">
            <Button
              size="large"
              onClick={() => setCancelModalVisible(false)}
              className="px-8 min-w-[120px]"
            >
              Không
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={handleCancelAppointment}
              loading={loading}
              className="bg-purple-600 hover:bg-purple-700 border-purple-600 px-8 min-w-[120px]"
            >
              Có
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AppointmentDetails;
